import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress, Chip, TextField,
  MenuItem, FormControlLabel, Switch, Dialog, DialogTitle, DialogContent,
  DialogActions, Button,
} from '@mui/material';
import { Home, EditRoad, AltRoute } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function LearnerRoutes() {
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [onlyBus, setOnlyBus] = useState(true);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirm, setConfirm] = useState(null); // { learner, newRoute }
  const [saving, setSaving] = useState(false);

  const routeName = (code) => routes.find(r => r.routeCode === code)?.name || code || '(none)';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (onlyBus) params.set('usesBus', 'true');
      if (search.trim()) params.set('search', search.trim());
      const [l, r] = await Promise.all([
        axios.get(`${API}/api/fleet/learners?${params.toString()}`, { headers: hdrs() }),
        axios.get(`${API}/api/fleet/routes`, { headers: hdrs() }),
      ]);
      setLearners(l.data); setRoutes(r.data); setError('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  }, [onlyBus, search]);

  useEffect(() => { load(); }, [onlyBus]); // eslint-disable-line

  // request a route change → open confirmation
  const requestChange = (learner, newRoute) => {
    if ((learner.routeCode || '') === newRoute) return;
    setConfirm({ learner, newRoute });
  };

  const applyChange = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/fleet/assign`,
        { admNo: confirm.learner.admNo, routeCode: confirm.newRoute }, { headers: hdrs() });
      setSuccess(`Route for ${confirm.learner.name} set to ${routeName(confirm.newRoute)}`);
      setConfirm(null);
      load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to change route'); setConfirm(null); }
    finally { setSaving(false); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center"><EditRoad sx={{ mr: 1, color: '#0277bd' }} /><Typography variant="h5" sx={{ color: '#0277bd' }}>Learner Routes</Typography></Box>
            <IconButton onClick={() => navigate('/fleet')} title="Fleet Dashboard"><Home /></IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Assign or change the route for each learner. Changing a route asks for confirmation before it is applied.
          </Typography>

          <Toast error={error} success={success} onClose={(k) => k === 'error' ? setError('') : setSuccess('')} />

          <Box display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap">
            <TextField size="small" label="Search name / adm no" value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') load(); }} sx={{ minWidth: 240 }} />
            <Button variant="outlined" size="small" onClick={load}>Search</Button>
            <FormControlLabel control={<Switch checked={onlyBus} onChange={e => setOnlyBus(e.target.checked)} />}
              label="Only learners using the bus" />
          </Box>

          {loading ? <Box textAlign="center" my={3}><CircularProgress /></Box> : learners.length === 0 ? (
            <Typography color="text.secondary">No learners match.</Typography>
          ) : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell><strong>Adm No</strong></TableCell><TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Class</strong></TableCell><TableCell><strong>Uses bus</strong></TableCell>
                <TableCell><strong>Pickup stage</strong></TableCell><TableCell><strong>Route</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {learners.map(l => (
                  <TableRow key={l.admNo} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{l.admNo}</TableCell>
                    <TableCell>{l.name}</TableCell>
                    <TableCell>{l.classCode}</TableCell>
                    <TableCell>
                      {l.usesBus
                        ? <Chip size="small" color="success" variant="outlined" label={l.usage || 'Yes'} />
                        : <Chip size="small" label="No" variant="outlined" />}
                    </TableCell>
                    <TableCell>{l.pickupName || '—'}</TableCell>
                    <TableCell>
                      <TextField select size="small" sx={{ minWidth: 190 }} value={l.routeCode || ''}
                        onChange={e => requestChange(l, e.target.value)}>
                        <MenuItem value=""><em>— None —</em></MenuItem>
                        {routes.map(r => <MenuItem key={r.routeCode} value={r.routeCode}>{r.routeCode} — {r.name}</MenuItem>)}
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Confirmation on route change */}
      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AltRoute color="primary" /> Confirm route change
        </DialogTitle>
        <DialogContent>
          {confirm && (
            <Typography>
              Change the route for <strong>{confirm.learner.name}</strong> ({confirm.learner.admNo})
              {' '}from <strong>{routeName(confirm.learner.routeCode)}</strong> to <strong>{routeName(confirm.newRoute)}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={applyChange} disabled={saving}>{saving ? 'Saving…' : 'Change route'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
